import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Admin.css';

// Set the base URL for all API requests
const API_BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://test3-0ar4.onrender.com';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [promoCodes, setPromoCodes] = useState([]);
  const [wagers, setWagers] = useState([]);
  const [coinflipGames, setCoinflipGames] = useState([]);
  const [jackpotGame, setJackpotGame] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatPaused, setChatPaused] = useState(false);
  
  // State for forms
  const [muteDuration, setMuteDuration] = useState(60);
  const [muteReason, setMuteReason] = useState('');
  const [tipUsername, setTipUsername] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoValue, setPromoValue] = useState('');
  const [promoMaxUses, setPromoMaxUses] = useState(-1);
  const [balanceUsername, setBalanceUsername] = useState('');
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceOperation, setBalanceOperation] = useState('add');
  const [verificationWagerId, setVerificationWagerId] = useState('');
  const [verificationData, setVerificationData] = useState(null);
  
  // NEW: System controls state
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceTitle, setMaintenanceTitle] = useState('RUSTYCOIN');
  const [maintenanceTagline, setMaintenanceTagline] = useState('');
  const [maintenanceSubTagline, setMaintenanceSubTagline] = useState('');
  const [maintenanceLaunch, setMaintenanceLaunch] = useState('April 10, 2026 18:00:00');
  const [announcementText, setAnnouncementText] = useState('');
  const [announcementActive, setAnnouncementActive] = useState(false);
  const [announcementColor, setAnnouncementColor] = useState('#3b82f6');
  const [rainAmount, setRainAmount] = useState(10);
  const [banReason, setBanReason] = useState('');
  
  // NEW: Automated Bot state
  const [botCoinflipEnabled, setBotCoinflipEnabled] = useState(false);
  const [botMinAmount, setBotMinAmount] = useState(1);
  const [botMaxAmount, setBotMaxAmount] = useState(100);

  // NEW: User Stats Management state
  const [statsUsername, setStatsUsername] = useState('');
  const [userStats, setUserStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsField, setStatsField] = useState('totalDeposited');
  const [statsAmount, setStatsAmount] = useState('');
  const [statsOperation, setStatsOperation] = useState('add');

  // Create axios instance with base URL
  const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
  });

  // Add request interceptor for auth
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Add response interceptor for error handling
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  // Load data based on active tab
  useEffect(() => {
    loadTabData();
  }, [activeTab]);

  const loadTabData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'dashboard':
          const statsRes = await api.get('/api/admin/dashboard');
          setStats(statsRes.data);
          break;
        case 'users':
          const usersRes = await api.get('/api/admin/users');
          setUsers(usersRes.data.users);
          break;
        case 'promocodes':
          const promosRes = await api.get('/api/admin/promo-codes');
          setPromoCodes(promosRes.data.promoCodes || []);
          break;
        case 'wagers':
          const wagersRes = await api.get('/api/admin/wagers/recent');
          setWagers(wagersRes.data.wagers || []);
          break;
        case 'bots':
          const cfRes = await api.get('/api/admin/games/coinflip');
          const jpRes = await api.get('/api/admin/games/jackpot');
          const botConfigRes = await api.get('/api/admin/system/coinflip-bot');
          setCoinflipGames(cfRes.data.games || []);
          setJackpotGame(jpRes.data.game);
          setBotCoinflipEnabled(botConfigRes.data.enabled || false);
          setBotMinAmount(botConfigRes.data.minAmount || 1);
          setBotMaxAmount(botConfigRes.data.maxAmount || 100);
          break;
        case 'system':
          const sysRes = await api.get('/api/admin/system/stats');
          const m = sysRes.data.maintenance || {};
          setMaintenanceEnabled(m.enabled || false);
          setMaintenanceTitle(m.title || 'RUSTYCOIN');
          setMaintenanceTagline(m.tagline || '');
          setMaintenanceSubTagline(m.subTagline || '');
          setMaintenanceLaunch(m.launchDate || 'April 10, 2026 18:00:00');
          setAnnouncementText(sysRes.data.announcement?.text || '');
          setAnnouncementActive(sysRes.data.announcement?.active || false);
          setAnnouncementColor(sysRes.data.announcement?.color || '#3b82f6');
          setChatPaused(sysRes.data.chatPaused);
          break;
        case 'withdrawals':
          const withdrawRes = await api.get('/api/admin/withdrawals');
          setWithdrawals(withdrawRes.data.withdrawals || []);
          break;
        default: break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const adjustBalance = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post(`/api/admin/users/${balanceUsername}/balance`, { 
        amount: parseFloat(balanceAmount),
        operation: balanceOperation,
        reason: 'Admin Adjustment'
      });
      alert(`Success! New balance: $${res.data.newBalance}`);
      setBalanceUsername('');
      setBalanceAmount('');
      loadTabData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  const createPromo = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/promo-codes', { 
        code: promoCode,
        value: parseFloat(promoValue),
        maxUses: parseInt(promoMaxUses)
      });
      alert('Promo code created successfully');
      setPromoCode('');
      setPromoValue('');
      setPromoMaxUses(-1);
      loadTabData();
    } catch (err) {
      alert('Failed to create promo code');
    }
  };

  const deletePromo = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await api.delete(`/api/admin/promo-codes/${id}`);
      loadTabData();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const sendAdminTip = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/tip', { 
        username: tipUsername,
        amount: parseFloat(tipAmount)
      });
      alert('Tip sent successfully');
      setTipUsername('');
      setTipAmount('');
      loadTabData();
    } catch (err) {
      alert('Failed to send tip');
    }
  };

  const toggleGlobalChat = async (enabled) => {
    try {
      await api.post('/api/admin/chat/global-mute', { enabled });
      setChatPaused(enabled);
      alert(`Chat ${enabled ? 'Paused' : 'Unpaused'}`);
    } catch (err) {
      alert('Failed');
    }
  };

  const triggerBotCoinflip = async (gameId) => {
    try {
      await api.post('/api/admin/bots/coinflip', { gameId });
      alert('Bot triggered for Coinflip');
      loadTabData();
    } catch (err) {
      alert('Failed to trigger bot');
    }
  };

  const instantBotCoinflip = async () => {
    try {
      await api.post('/api/admin/bots/coinflip-create');
      alert('Instant bot game created!');
      loadTabData();
    } catch (err) {
      alert('Failed to create instant bot game');
    }
  };

  const triggerBotJackpot = async () => {
    try {
      await api.post('/api/admin/bots/jackpot');
      alert('Bot triggered for Jackpot');
      loadTabData();
    } catch (err) {
      alert('Failed to trigger bot');
    }
  };

  const saveBotConfig = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/system/coinflip-bot', {
        enabled: botCoinflipEnabled,
        minAmount: parseFloat(botMinAmount),
        maxAmount: parseFloat(botMaxAmount)
      });
      alert('Bot configuration saved!');
      loadTabData();
    } catch (err) {
      alert('Failed to save bot configuration');
    }
  };

  const banUser = async (userId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY ban this user? They will be logged out and cannot return.')) return;
    try {
      await api.post(`/api/admin/users/${userId}/ban`, { reason: banReason });
      alert('User banned');
      setBanReason('');
      loadTabData();
    } catch (err) {
      alert('Ban failed');
    }
  };

  const unbanUser = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/unban`);
      alert('User unbanned');
      loadTabData();
    } catch (err) {
      alert('Unban failed');
    }
  };

  const rigUser = async (userId, outcome) => {
    try {
      await api.post(`/api/admin/users/${userId}/rig`, { outcome });
      alert(`User result forced to: ${outcome || 'Random'}`);
      loadTabData();
    } catch (err) {
      alert('Rigging failed');
    }
  };

  const toggleMaintenance = async (enabled) => {
    try {
      await api.post('/api/admin/system/maintenance', { 
        enabled,
        title: maintenanceTitle,
        tagline: maintenanceTagline,
        subTagline: maintenanceSubTagline,
        launchDate: maintenanceLaunch
      });
      setMaintenanceEnabled(enabled);
      alert(`Maintenance mode ${enabled ? 'ENABLED' : 'DISABLED'}`);
    } catch (err) {
      alert('Failed to update maintenance mode');
    }
  };

  const saveMaintenanceDetails = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/system/maintenance', { 
        enabled: maintenanceEnabled,
        title: maintenanceTitle,
        tagline: maintenanceTagline,
        subTagline: maintenanceSubTagline,
        launchDate: maintenanceLaunch
      });
      alert('Maintenance details saved and updated live!');
    } catch (err) {
      alert('Save failed');
    }
  };

  const updateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/system/announcement', { 
        text: announcementText,
        active: announcementActive,
        color: announcementColor
      });
      alert('Announcement updated');
    } catch (err) {
      alert('Failed to update announcement');
    }
  };

  const manualRain = async () => {
    try {
      await api.post('/api/admin/system/start-rain', { amount: rainAmount });
      alert(`Rain started with $${rainAmount}`);
    } catch (err) {
      alert('Failed to start rain');
    }
  };

  const clearChat = async () => {
    if (!window.confirm('Are you sure you want to CLEAR the entire chat history for everyone?')) return;
    try {
      await api.post('/api/admin/chat/clear');
      alert('Chat cleared');
    } catch (err) {
      alert('Failed to clear chat');
    }
  };

  const updateWithdrawalStatus = async (id, status) => {
    const notes = window.prompt(`Reason for ${status}?`, '');
    if (notes === null) return;
    try {
      await api.post(`/api/admin/withdrawals/${id}/status`, { status, adminNotes: notes });
      alert(`Withdrawal ${status}`);
      loadTabData();
    } catch (err) {
      alert('Failed to update withdrawal status');
    }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amt || 0);

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Control Center</h1>
        <button className="refresh-btn" onClick={loadTabData}>Refresh</button>
      </div>

      <div className="admin-tabs">
        {['dashboard', 'users', 'withdrawals', 'userstats', 'chat', 'system', 'promocodes', 'tips', 'balance', 'bots', 'wagers'].map(t => (
          <button key={t} className={activeTab === t ? 'active' : ''} onClick={() => setActiveTab(t)}>
            {t === 'userstats' ? 'User Stats' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="admin-content">
        {loading && <div className="admin-loading">Loading components...</div>}
        
        {!loading && activeTab === 'dashboard' && (
          <div className="stats-grid">
            <div className="stat-card"><h3>Users</h3><p>{stats.totalUsers}</p></div>
            <div className="stat-card"><h3>Platform Balance</h3><p>{formatCurrency(stats.totalBalance)}</p></div>
            <div className="stat-card"><h3>Promo Balance</h3><p>{formatCurrency(stats.totalPromoBalance)}</p></div>
            <div className="stat-card"><h3>Total Wagered</h3><p>{formatCurrency(stats.totalWagered)}</p></div>
            <div className="stat-card"><h3>Normal Wagered</h3><p>{formatCurrency(stats.totalNormalWagered)}</p></div>
            <div className="stat-card"><h3>Promo Wagered</h3><p>{formatCurrency(stats.totalPromoWagered)}</p></div>
            <div className="stat-card"><h3>Total Deposited</h3><p>{formatCurrency(stats.totalDeposited)}</p></div>
            <div className="stat-card"><h3>Total Withdrawn</h3><p>{formatCurrency(stats.totalWithdrawn)}</p></div>
            <div className="stat-card"><h3>Registrations Today</h3><p>{stats.todayRegistrations}</p></div>
          </div>
        )}

        {!loading && activeTab === 'users' && (
          <div className="section">
            <h2>User Management</h2>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>User</th><th>Balance</th><th>Promo Bal</th><th>Wagered</th><th>Roles</th><th>Actions</th></tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u._id}>
                      <td>
                        <div>{u.username}</div>
                        <div style={{ display: 'flex', gap: '3px', marginTop: '4px' }}>
                          <button onClick={() => rigUser(u._id, 'win')} style={{ padding: '2px 4px', fontSize: '9px', background: u.rigOutcome === 'win' ? '#22c55e' : '#1e293b', border: '1px solid #334155', borderRadius: '3px', color: 'white' }}>W</button>
                          <button onClick={() => rigUser(u._id, 'loss')} style={{ padding: '2px 4px', fontSize: '9px', background: u.rigOutcome === 'loss' ? '#ef4444' : '#1e293b', border: '1px solid #334155', borderRadius: '3px', color: 'white' }}>L</button>
                          <button onClick={() => rigUser(u._id, null)} style={{ padding: '2px 4px', fontSize: '9px', background: '#1e293b', border: '1px solid #334155', borderRadius: '3px', color: 'white' }}>R</button>
                        </div>
                      </td>
                      <td>{formatCurrency(u.balance)}</td>
                      <td>{formatCurrency(u.promoBalance)}</td>
                      <td>{formatCurrency(u.totalWagered)}</td>
                      <td>
                        <div className="role-tags" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {(u.roles || []).map(r => (
                            <span key={r} style={{ padding: '2px 6px', background: r === 'admin' ? '#ef4444' : '#3b82f6', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>{r}</span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons" style={{ display: 'flex', gap: '5px' }}>
                          <button 
                            className="btn-blue"
                            style={{ padding: '4px 8px' }}
                            onClick={async () => {
                              const amount = window.prompt(`Update balance for ${u.username}`, '0');
                              if (amount !== null) {
                                await api.post(`/api/admin/users/${u._id}/balance`, { amount: parseFloat(amount), operation: 'add' });
                                loadTabData();
                              }
                            }}
                          >
                            $
                          </button>

                          {!(u.roles?.includes('admin') || u.isAdmin) ? (
                            <button 
                              className="btn-purple"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={async () => {
                                if (window.confirm(`Promote ${u.username} to Admin?`)) {
                                  await api.post(`/api/admin/users/${u._id}/add-role`, { role: 'admin' });
                                  loadTabData();
                                }
                              }}
                            >
                              +Adm
                            </button>
                          ) : (
                            <button 
                              className="btn-red"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={async () => {
                                if (window.confirm(`Remove Admin status from ${u.username}?`)) {
                                  await api.post(`/api/admin/users/${u._id}/remove-role`, { role: 'admin' });
                                  loadTabData();
                                }
                              }}
                            >
                              -Adm
                            </button>
                          )}

                          {!u.roles?.includes('Streamer') ? (
                            <button 
                              className="btn-blue"
                              style={{ padding: '4px 8px', fontSize: '11px', background: '#3b82f6' }}
                              onClick={async () => {
                                if (window.confirm(`Give ${u.username} Streamer role?`)) {
                                  await api.post(`/api/admin/users/${u._id}/add-role`, { role: 'Streamer' });
                                  loadTabData();
                                }
                              }}
                            >
                              +Strm
                            </button>
                          ) : (
                            <button 
                              className="btn-red"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={async () => {
                                if (window.confirm(`Remove Streamer role from ${u.username}?`)) {
                                  await api.post(`/api/admin/users/${u._id}/remove-role`, { role: 'Streamer' });
                                  loadTabData();
                                }
                              }}
                            >
                              -Strm
                            </button>
                          )}

                          {!u.isChatMuted ? (
                            <button 
                              className="btn-warn"
                              onClick={async () => {
                                const reason = window.prompt('Reason for mute?', 'Violation of chat rules');
                                if (reason === null) return;
                                await api.post(`/api/admin/users/${u._id}/mute`, { duration: 60, reason });
                                loadTabData();
                              }}
                            >
                              Mute
                            </button>
                          ) : (
                            <button 
                              className="btn-green"
                              onClick={async () => {
                                await api.post(`/api/admin/users/${u._id}/unmute`);
                                loadTabData();
                              }}
                            >
                              Unmute
                            </button>
                          )}
                          
                          {!u.isBanned ? (
                            <button 
                              className="btn-red"
                              onClick={() => {
                                const reason = window.prompt('Reason for ban?', 'Violation of terms of service');
                                if (reason === null) return;
                                setBanReason(reason);
                                banUser(u._id);
                              }}
                            >
                              Ban
                            </button>
                          ) : (
                            <button 
                              className="btn-green"
                              onClick={() => unbanUser(u._id)}
                            >
                              Unban
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && activeTab === 'chat' && (
          <div className="section">
            <h2>Chat Moderation</h2>
            <div className="chat-control-grid">
              <div className="card">
                <h3>Global Chat Pause</h3>
                <p>Prevent all users from sending messages.</p>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button className="btn-red" onClick={() => toggleGlobalChat(true)}>PAUSE CHAT</button>
                  <button className="btn-green" onClick={() => toggleGlobalChat(false)}>UNPAUSE CHAT</button>
                </div>
              </div>
              
              <div className="card">
                <h3>Clear Chat History</h3>
                <p>Delete all messages for all users.</p>
                <button className="btn-red" onClick={clearChat} style={{ marginTop: '10px' }}>CLEAR ENTIRE CHAT</button>
              </div>
            </div>
          </div>
        )}

        {!loading && activeTab === 'system' && (
          <div className="section">
            <h2>System Controls</h2>
            
            <div className="admin-grid">
              <div className="card full-width">
                <h3>Site Maintenance & "Coming Soon" Branding</h3>
                <p>Configure the high-end maintenance page appearance. Updates happen in real-time.</p>
                
                <div className="admin-form" style={{ marginTop: '20px' }}>
                  <div className="form-group">
                    <label>Main Title (Gradients Applied)</label>
                    <input value={maintenanceTitle} onChange={e => setMaintenanceTitle(e.target.value)} placeholder="e.g. RUSTYCOIN" />
                  </div>
                  <div className="form-group">
                    <label>Tagline (White text)</label>
                    <input value={maintenanceTagline} onChange={e => setMaintenanceTagline(e.target.value)} placeholder="The next generation..." />
                  </div>
                  <div className="form-group">
                    <label>Sub-Tagline (Purple glow)</label>
                    <input value={maintenanceSubTagline} onChange={e => setMaintenanceSubTagline(e.target.value)} placeholder="Your luck starts here..." />
                  </div>
                  <div className="form-group">
                    <label>Launch Date (For Countdown)</label>
                    <input value={maintenanceLaunch} onChange={e => setMaintenanceLaunch(e.target.value)} placeholder="April 10, 2026 18:00:00" />
                  </div>
                  
                  <div className="form-row" style={{ marginTop: '10px' }}>
                    <button className="btn-blue" onClick={saveMaintenanceDetails}>SAVE & UPDATE TEXT</button>
                    <button 
                      className={maintenanceEnabled ? 'btn-red' : 'btn-green'} 
                      onClick={() => toggleMaintenance(!maintenanceEnabled)}
                      style={{ marginLeft: '10px' }}
                    >
                      {maintenanceEnabled ? 'GO LIVE (DISABLE MAINTENANCE)' : 'ENABLE MAINTENANCE MODE'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3>Manual Rain</h3>
                <p>Distribute currency to active users.</p>
                <div className="admin-form-inline" style={{ marginTop: '10px' }}>
                  <input 
                    type="number" 
                    value={rainAmount} 
                    onChange={e => setRainAmount(e.target.value)} 
                    placeholder="Rain Amount ($)"
                  />
                  <button className="btn-blue" onClick={manualRain}>START RAIN</button>
                </div>
              </div>

              <div className="card full-width">
                <h3>Announcement Banner</h3>
                <form onSubmit={updateAnnouncement} className="admin-form">
                  <div className="form-group">
                    <label>Banner Text</label>
                    <textarea 
                      value={announcementText} 
                      onChange={e => setAnnouncementText(e.target.value)} 
                      placeholder="Enter announcement text..."
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Active</label>
                      <input 
                        type="checkbox" 
                        checked={announcementActive} 
                        onChange={e => setAnnouncementActive(e.target.checked)} 
                      />
                    </div>
                    <div className="form-group">
                      <label>Color</label>
                      <input 
                        type="color" 
                        value={announcementColor} 
                        onChange={e => setAnnouncementColor(e.target.value)} 
                      />
                    </div>
                    <button type="submit" className="btn-blue">SAVE ANNOUNCEMENT</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {!loading && activeTab === 'promocodes' && (
          <div className="section">
            <h2>Promo Code Management</h2>
            <form className="admin-form" onSubmit={createPromo}>
              <input placeholder="Promo Code" value={promoCode} onChange={e => setPromoCode(e.target.value)} required />
              <input type="number" placeholder="Value ($)" value={promoValue} onChange={e => setPromoValue(e.target.value)} required />
              <input type="number" placeholder="Max Uses (-1 for unlim)" value={promoMaxUses} onChange={e => setPromoMaxUses(e.target.value)} required />
              <button type="submit">Create Promo</button>
            </form>
            
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Code</th><th>Value</th><th>Uses</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {promoCodes.map(p => (
                    <tr key={p._id}>
                      <td>{p.code}</td>
                      <td>{formatCurrency(p.value)}</td>
                      <td>{p.usedCount} / {p.maxUses === -1 ? '∞' : p.maxUses}</td>
                      <td><span className={p.active ? 'active-badge' : 'muted-badge'}>{p.active ? 'Active' : 'Inactive'}</span></td>
                      <td><button className="delete-btn" onClick={() => deletePromo(p._id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && activeTab === 'tips' && (
          <div className="section">
            <h2>Admin Tip (Spawn Money)</h2>
            <form className="admin-form" onSubmit={sendAdminTip}>
              <input placeholder="Recipient Username" value={tipUsername} onChange={e => setTipUsername(e.target.value)} required />
              <input type="number" step="0.01" placeholder="Amount ($)" value={tipAmount} onChange={e => setTipAmount(e.target.value)} required />
              <button type="submit">Send Admin Tip</button>
            </form>
          </div>
        )}

        {!loading && activeTab === 'balance' && (
          <div className="section">
            <h2>Balance Adjustments</h2>
            <form className="admin-form" onSubmit={adjustBalance}>
              <input placeholder="Target Username" value={balanceUsername} onChange={e => setBalanceUsername(e.target.value)} required />
              <input type="number" step="0.01" placeholder="Amount ($)" value={balanceAmount} onChange={e => setBalanceAmount(e.target.value)} required />
              <select value={balanceOperation} onChange={e => setBalanceOperation(e.target.value)}>
                <option value="add">Add</option>
                <option value="subtract">Subtract</option>
                <option value="set">Set Exact</option>
              </select>
              <button type="submit">Execute Adjustment</button>
            </form>
          </div>
        )}

        {!loading && activeTab === 'bots' && (
          <div className="bots-control section">
            <h2>Bot Management</h2>
            
            <div className="card full-width" style={{marginBottom: '20px', border: '1px solid #3b82f6', background: 'rgba(59, 130, 246, 0.05)'}}>
              <h3>Automated 5-Minute Coinflip Bot</h3>
              <p>When enabled, a bot will automatically create a new Coinflip game every 5 minutes.</p>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginTop: '15px' }}>
                <form onSubmit={saveBotConfig} className="admin-form" style={{margin: 0, flex: 1}}>
                  <div className="form-row" style={{display: 'flex', gap: '20px', alignItems: 'center'}}>
                    <div className="form-group" style={{margin: 0}}>
                      <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                        <input 
                          type="checkbox" 
                          checked={botCoinflipEnabled} 
                          onChange={e => setBotCoinflipEnabled(e.target.checked)} 
                          style={{width: '20px', height: '20px'}}
                        />
                        <span>Bot Enabled</span>
                      </label>
                    </div>
                    <div className="form-group" style={{margin: 0}}>
                      <label>Min Amount ($)</label>
                      <input 
                        type="number" 
                        value={botMinAmount} 
                        onChange={e => setBotMinAmount(e.target.value)} 
                        style={{width: '60px'}}
                      />
                    </div>
                    <div className="form-group" style={{margin: 0}}>
                      <label>Max Amount ($)</label>
                      <input 
                        type="number" 
                        value={botMaxAmount} 
                        onChange={e => setBotMaxAmount(e.target.value)} 
                        style={{width: '60px'}}
                      />
                    </div>
                    <button type="submit" className="btn-blue">Update Config</button>
                  </div>
                </form>

                <div className="instant-actions" style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={instantBotCoinflip} className="btn-purple" style={{ padding: '10px 20px', fontWeight: 'bold' }}>
                    ⚡ INSTANT BOT COINFLIP
                  </button>
                </div>
              </div>
            </div>

            <div className="subsection">
              <h3>Active Coinflip Games</h3>
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead><tr><th>Creator</th><th>Amount</th><th>Status</th><th>Toggle</th></tr></thead>
                  <tbody>
                    {coinflipGames.map(g => (
                      <tr key={g.id}>
                        <td>{g.creator.username}</td>
                        <td>{formatCurrency(g.amount)}</td>
                        <td>{g.status}</td>
                        <td>{g.status === 'waiting' && <button onClick={() => triggerBotCoinflip(g.id)}>Force Join</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="subsection" style={{marginTop: '20px'}}>
              <h3>Jackpot</h3>
              {jackpotGame && (
                <div className="stat-card" style={{maxWidth: '300px'}}>
                  <p>Pot: {formatCurrency(jackpotGame.totalPot)}</p>
                  <p>Players: {jackpotGame.players.length}</p>
                  <button className="btn-green" onClick={triggerBotJackpot} style={{marginTop: '10px'}}>Force Bot Entry</button>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && activeTab === 'wagers' && (
          <div className="section">
            <h2>Recent Platform Wagers</h2>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>User</th><th>Game</th><th>Amount</th><th>Outcome</th><th>Time</th></tr></thead>
                <tbody>
                  {wagers.map(w => (
                    <tr key={w._id}>
                      <td>{w.userId?.username}</td>
                      <td>{w.gameType || w.game}</td>
                      <td>{formatCurrency(w.amount)}</td>
                      <td><span className={w.outcome === 'win' || w.won ? 'profit-positive' : 'profit-negative'}>{w.outcome === 'win' || w.won ? 'WIN' : 'LOSS'}</span></td>
                      <td>{new Date(w.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && activeTab === 'userstats' && (
          <div className="section">
            <h2>User Stats Management</h2>
            <p style={{color: '#94a3b8', marginBottom: '20px'}}>Manage deposited, withdrawn, and wagering stats per user. Look up a user first, then adjust their values.</p>
            
            {/* Lookup Form */}
            <div className="card" style={{marginBottom: '20px'}}>
              <h3>Lookup User</h3>
              <div className="admin-form-inline" style={{marginTop: '10px'}}>
                <input 
                  placeholder="Username or User ID" 
                  value={statsUsername} 
                  onChange={e => setStatsUsername(e.target.value)} 
                />
                <button 
                  className="btn-blue" 
                  onClick={async () => {
                    if (!statsUsername) return;
                    setStatsLoading(true);
                    try {
                      const res = await api.get(`/api/admin/users/${statsUsername}/stats`);
                      setUserStats(res.data);
                    } catch (err) {
                      alert(err.response?.data?.error || 'User not found');
                      setUserStats(null);
                    }
                    setStatsLoading(false);
                  }}
                >
                  LOOKUP
                </button>
              </div>
            </div>

            {statsLoading && <div className="admin-loading">Loading user stats...</div>}

            {userStats && !statsLoading && (
              <>
                {/* Current Stats Display */}
                <div className="card" style={{marginBottom: '20px'}}>
                  <h3>Current Stats for: <span style={{color: '#ffb703'}}>{userStats.username}</span></h3>
                  <div className="stats-grid" style={{marginTop: '15px'}}>
                    <div className="stat-card"><h3>Balance</h3><p>{formatCurrency(userStats.balance)}</p></div>
                    <div className="stat-card"><h3>Promo Balance</h3><p>{formatCurrency(userStats.promoBalance)}</p></div>
                    <div className="stat-card" style={{borderLeft: '3px solid #22c55e'}}><h3>Total Deposited</h3><p>{formatCurrency(userStats.totalDeposited)}</p></div>
                    <div className="stat-card" style={{borderLeft: '3px solid #ef4444'}}><h3>Total Withdrawn</h3><p>{formatCurrency(userStats.totalWithdrawn)}</p></div>
                    <div className="stat-card" style={{borderLeft: '3px solid #3b82f6'}}><h3>Total Wagered</h3><p>{formatCurrency(userStats.totalWagered)}</p></div>
                    <div className="stat-card" style={{borderLeft: '3px solid #a855f7'}}><h3>Normal Wagered</h3><p>{formatCurrency(userStats.normalWagered)}</p></div>
                    <div className="stat-card" style={{borderLeft: '3px solid #ffb703'}}><h3>Promo Wagered</h3><p>{formatCurrency(userStats.promoWagered)}</p></div>
                  </div>
                </div>

                {/* Adjust Stats Form */}
                <div className="card" style={{marginBottom: '20px'}}>
                  <h3>Adjust Stats</h3>
                  <div className="admin-form" style={{marginTop: '15px'}}>
                    <div className="form-row" style={{display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap'}}>
                      <div className="form-group" style={{flex: 1, minWidth: '150px'}}>
                        <label>Field</label>
                        <select value={statsField} onChange={e => setStatsField(e.target.value)}>
                          <option value="totalDeposited">Total Deposited</option>
                          <option value="totalWithdrawn">Total Withdrawn</option>
                          <option value="normalWagered">Normal Wagered</option>
                          <option value="promoWagered">Promo Wagered</option>
                        </select>
                      </div>
                      <div className="form-group" style={{flex: 1, minWidth: '100px'}}>
                        <label>Operation</label>
                        <select value={statsOperation} onChange={e => setStatsOperation(e.target.value)}>
                          <option value="add">Add</option>
                          <option value="subtract">Subtract</option>
                          <option value="set">Set Exact</option>
                          <option value="reset">Reset to 0</option>
                        </select>
                      </div>
                      <div className="form-group" style={{flex: 1, minWidth: '100px'}}>
                        <label>Amount ($)</label>
                        <input 
                          type="number" 
                          step="0.01" 
                          value={statsAmount} 
                          onChange={e => setStatsAmount(e.target.value)} 
                          placeholder="0.00"
                          disabled={statsOperation === 'reset'}
                        />
                      </div>
                      <button 
                        className="btn-blue" 
                        style={{height: '42px'}}
                        onClick={async () => {
                          const fieldMap = {
                            totalDeposited: 'deposited',
                            totalWithdrawn: 'withdrawn',
                            normalWagered: 'normal-wagered',
                            promoWagered: 'promo-wagered'
                          };
                          const endpoint = fieldMap[statsField];
                          try {
                            const res = await api.post(`/api/admin/users/${userStats.userId}/stats/${endpoint}`, {
                              amount: parseFloat(statsAmount) || 0,
                              operation: statsOperation
                            });
                            alert(`Success! ${statsField} updated.`);
                            // Refresh stats
                            const refresh = await api.get(`/api/admin/users/${userStats.userId}/stats`);
                            setUserStats(refresh.data);
                            setStatsAmount('');
                          } catch (err) {
                            alert(err.response?.data?.error || 'Update failed');
                          }
                        }}
                      >
                        APPLY
                      </button>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="card">
                  <h3>Quick Actions</h3>
                  <div style={{display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap'}}>
                    <button 
                      className="btn-red" 
                      onClick={async () => {
                        if (!window.confirm(`Reset ALL stats for ${userStats.username}? This sets deposited, withdrawn, and all wagering to $0.`)) return;
                        try {
                          await api.post(`/api/admin/users/${userStats.userId}/stats/reset-all`, { fields: [] });
                          alert('All stats reset to $0');
                          const refresh = await api.get(`/api/admin/users/${userStats.userId}/stats`);
                          setUserStats(refresh.data);
                        } catch (err) {
                          alert('Reset failed');
                        }
                      }}
                    >
                      RESET ALL STATS
                    </button>
                    <button 
                      className="btn-warn" 
                      onClick={async () => {
                        if (!window.confirm(`Reset wagering stats (normal + promo) for ${userStats.username}?`)) return;
                        try {
                          await api.post(`/api/admin/users/${userStats.userId}/stats/reset-all`, { fields: ['normalWagered', 'promoWagered'] });
                          alert('Wagering stats reset');
                          const refresh = await api.get(`/api/admin/users/${userStats.userId}/stats`);
                          setUserStats(refresh.data);
                        } catch (err) {
                          alert('Reset failed');
                        }
                      }}
                    >
                      RESET WAGERING ONLY
                    </button>
                    <button 
                      className="btn-warn" 
                      onClick={async () => {
                        if (!window.confirm(`Reset deposit/withdraw stats for ${userStats.username}?`)) return;
                        try {
                          await api.post(`/api/admin/users/${userStats.userId}/stats/reset-all`, { fields: ['totalDeposited', 'totalWithdrawn'] });
                          alert('Deposit/withdraw stats reset');
                          const refresh = await api.get(`/api/admin/users/${userStats.userId}/stats`);
                          setUserStats(refresh.data);
                        } catch (err) {
                          alert('Reset failed');
                        }
                      }}
                    >
                      RESET DEPOSITS/WITHDRAWALS
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {!loading && activeTab === 'withdrawals' && (
          <div className="section">
            <h2>Withdrawal Requests</h2>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Address</th>
                    <th>Summary</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.length === 0 ? (
                    <tr><td colSpan="7" style={{textAlign: 'center'}}>No withdrawal requests found.</td></tr>
                  ) : (
                    withdrawals.map(w => (
                      <tr key={w._id}>
                        <td>{w.username}</td>
                        <td>{formatCurrency(w.amount)}</td>
                        <td>{w.method}</td>
                        <td style={{fontSize: '11px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={w.address}>{w.address}</td>
                        <td style={{fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={w.adminNotes}>{w.adminNotes}</td>
                        <td>
                          <span className={`status-badge ${w.status}`}>
                            {w.status.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          {w.status === 'pending' && (
                            <div className="action-buttons" style={{display: 'flex', gap: '5px'}}>
                              <button className="btn-green" onClick={() => updateWithdrawalStatus(w._id, 'approved')}>Approve</button>
                              <button className="btn-red" onClick={() => updateWithdrawalStatus(w._id, 'rejected')}>Reject</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;

